import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Crown,
  Users,
  Wallet,
  TrendingUp,
  Search,
  LogOut,
  CheckCircle2,
  XCircle,
  Plus,
  Trash2,
  Copy,
  Image as ImageIcon,
  Globe,
  ArrowDownToLine,
  Coins,
  Pencil,
  Power,
  QrCode,
  Activity,
  RefreshCw,
  UserCheck,
  Clock3,
  ChevronLeft,
  ChevronRight,
  Server,
  CreditCard,
  Ban,
  Check,
  X,
  ExternalLink,
  Loader2,
} from "lucide-react";
import { apiRequest } from "../api";

const API_BASE = (function () {
  try {
    const raw = String(
      process.env.REACT_APP_API_BASE_URL ||
        "http://localhost:5000"
    );

    return new URL(raw).origin;
  } catch (e) {
    return "http://localhost:5000";
  }
})();

const API_ORIGIN = API_BASE;

const MAX_QR_BYTES = 2 * 1024 * 1024;

const ALLOWED_IMAGE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
];

const NETWORK_OPTIONS = [
  {
    value: "TRC20",
    label: "USDT TRC20 (Tron)",
  },
  {
    value: "ERC20",
    label: "USDT ERC20 (Ethereum)",
  },
  {
    value: "BEP20",
    label: "USDT BEP20 (BNB Smart Chain)",
  },
  {
    value: "TRON",
    label: "TRON",
  },
];

function networkLabel(net) {
  const found = NETWORK_OPTIONS.find(function (n) {
    return n.value === net;
  });

  return found ? found.label : net;
}

function safeMediaUrl(raw) {
  if (typeof raw !== "string") {
    return null;
  }

  const value = raw.trim();

  if (!value) {
    return null;
  }

  if (/^\/[^/\\]/.test(value)) {
    if (value.includes("..")) {
      return null;
    }

    return API_BASE + value;
  }

  let url;

  try {
    url = new URL(value);
  } catch (e) {
    return null;
  }

  if (
    url.protocol !== "http:" &&
    url.protocol !== "https:"
  ) {
    return null;
  }

  if (!API_ORIGIN || url.origin !== API_ORIGIN) {
    return null;
  }

  return url.toString();
}

function pathId(id) {
  return encodeURIComponent(String(id || ""));
}

function describeError(err, fallback) {
  const fb =
    fallback || "Something went wrong. Try again.";

  const message =
    err && typeof err.message === "string"
      ? err.message.trim()
      : "";

  if (!message) {
    return fb;
  }

  if (message.length > 200) {
    return fb;
  }

  return message;
}

function isAuthError(err) {
  const status =
    err && err.status
      ? err.status
      : err && err.response
        ? err.response.status
        : null;

  return status === 401 || status === 403;
}

const STAT_THEMES = {
  blue: {
    bg: "from-[#172A63]/80 via-[#101728] to-[#0B101C]",
    ring: "border-[#2D5BFF]/25",
    icon: "bg-[#2D5BFF]/15 text-[#6E93FF]",
    glow: "bg-[#2D5BFF]/10",
  },

  green: {
    bg: "from-[#0D493B]/80 via-[#101C1B] to-[#0B1018]",
    ring: "border-[#16D9A0]/25",
    icon: "bg-[#16D9A0]/15 text-[#3FE0B4]",
    glow: "bg-[#16D9A0]/10",
  },

  gold: {
    bg: "from-[#513B10]/80 via-[#1B1811] to-[#0F1119]",
    ring: "border-[#F5B942]/25",
    icon: "bg-[#F5B942]/15 text-[#F5B942]",
    glow: "bg-[#F5B942]/10",
  },
};

function AdminStatCard(props) {
  const Icon = props.icon;
  const theme = STAT_THEMES[props.theme];

  return (
    <div
      className={
        "group relative overflow-hidden rounded-3xl border bg-gradient-to-br p-5 " +
        theme.ring +
        " " +
        theme.bg +
        " transition-all duration-300 hover:-translate-y-0.5 hover:border-white/15"
      }
    >
      <div
        className={
          "pointer-events-none absolute -right-8 -top-8 h-28 w-28 rounded-full blur-3xl " +
          theme.glow
        }
      />

      <div className="relative">
        <div className="mb-5 flex items-center justify-between">
          <div
            className={
              "flex h-11 w-11 items-center justify-center rounded-2xl " +
              theme.icon
            }
          >
            <Icon size={19} strokeWidth={2.2} />
          </div>

          <Activity
            size={15}
            className="text-white/20 transition-colors group-hover:text-white/40"
          />
        </div>

        <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8D99B3]">
          {props.label}
        </div>

        <div className="mt-1 break-words text-2xl font-extrabold tracking-tight text-white">
          {props.value}
        </div>
      </div>
    </div>
  );
}

function SectionHeader(props) {
  const Icon = props.icon;

  return (
    <div className="mb-5 flex items-start justify-between gap-4">
      <div className="flex min-w-0 items-start gap-3">
        <div
          className={
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl " +
            props.iconClass
          }
        >
          <Icon size={18} />
        </div>

        <div className="min-w-0">
          <h2 className="text-[15px] font-bold tracking-tight text-white">
            {props.title}
          </h2>

          <p className="mt-1 text-[11px] leading-relaxed text-[#727E98]">
            {props.description}
          </p>
        </div>
      </div>

      {props.badge ? (
        <span
          className={
            props.badgeClass ||
            "shrink-0 rounded-full bg-white/5 px-2.5 py-1 text-[10px] font-semibold text-[#9AA6C3]"
          }
        >
          {props.badge}
        </span>
      ) : null}
    </div>
  );
}

function ScreenshotLink(props) {
  return React.createElement(
    "a",
    {
      href: props.href,
      target: "_blank",
      rel: "noreferrer",
      className:
        "mt-3 inline-flex items-center gap-1.5 rounded-lg border border-[#2A3149] bg-[#0B101A] px-2.5 py-1.5 text-[10px] font-semibold text-[#6E93FF] transition hover:border-[#5B8DEF]/50 hover:bg-[#5B8DEF]/10",
    },
    React.createElement(ImageIcon, { size: 12 }),
    "View screenshot",
    React.createElement(ExternalLink, { size: 10 })
  );
}

/* =========================================================
   MOBILE USER CARD
========================================================= */

function MobileUserCard(props) {
  const user = props.user;
  const busy = props.busy;
  const suspended = user.status === "suspended";

  const initials = String(user.name || "U")
    .charAt(0)
    .toUpperCase();

  const balance = Number(
    user.usdt_balance || 0
  ).toFixed(2);

  return (
    <div
      className={
        "group relative overflow-hidden rounded-2xl border p-4 transition-all duration-300 " +
        (suspended
          ? "border-[#E5566B]/10 bg-[#0F111A]"
          : "border-white/[0.07] bg-[#101623] hover:-translate-y-0.5 hover:border-[#5B8DEF]/25 hover:bg-[#111827]")
      }
    >
      {/* subtle glow */}
      <div
        className={
          "pointer-events-none absolute -right-16 -top-16 h-36 w-36 rounded-full blur-3xl " +
          (suspended
            ? "bg-[#E5566B]/5"
            : "bg-[#5B8DEF]/7")
        }
      />

      <div className="relative">
        {/* User identity */}
        <div className="flex items-center gap-3">
          <div
            className={
              "flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br text-sm font-extrabold shadow-inner " +
              (suspended
                ? "from-[#E5566B]/15 to-[#7F3140]/10 text-[#F07889]"
                : "from-[#5B8DEF]/20 to-[#8B5CF6]/15 text-[#8EAFFF]")
            }
          >
            {initials}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-2">
              <h3 className="min-w-0 truncate text-[13px] font-bold text-white">
                {user.name || "Unknown"}
              </h3>

              <span
                className={
                  "hidden shrink-0 rounded-full border px-2 py-0.5 text-[8px] font-bold uppercase tracking-wider xs:inline-flex " +
                  (suspended
                    ? "border-[#E5566B]/15 bg-[#E5566B]/10 text-[#F07889]"
                    : "border-[#3FBF8F]/15 bg-[#3FBF8F]/10 text-[#3FBF8F]")
                }
              >
                {suspended ? "Suspended" : "Active"}
              </span>
            </div>

            <div className="mt-1 truncate text-[10px] text-[#68748E]">
              {user.email || "No email"}
            </div>
          </div>

          {/* Status dot */}
          <div
            className={
              "h-2 w-2 shrink-0 rounded-full " +
              (suspended
                ? "bg-[#E5566B]"
                : "bg-[#3FBF8F] shadow-[0_0_9px_rgba(63,191,143,0.75)]")
            }
          />
        </div>

        {/* Mobile status */}
        <div className="mt-3 flex items-center gap-2 xs:hidden">
          <span
            className={
              "inline-flex items-center gap-1.5 rounded-full border px-2 py-1 text-[8px] font-bold uppercase tracking-wider " +
              (suspended
                ? "border-[#E5566B]/15 bg-[#E5566B]/10 text-[#F07889]"
                : "border-[#3FBF8F]/15 bg-[#3FBF8F]/10 text-[#3FBF8F]")
            }
          >
            <span
              className={
                "h-1.5 w-1.5 rounded-full " +
                (suspended
                  ? "bg-[#E5566B]"
                  : "bg-[#3FBF8F]")
              }
            />

            {suspended ? "Suspended" : "Active"}
          </span>
        </div>

        {/* Balance / VIP */}
        <div className="mt-4 grid grid-cols-2 gap-2">
          <div className="rounded-xl border border-[#3FBF8F]/10 bg-[#3FBF8F]/[0.045] p-3">
            <div className="flex items-center gap-1.5 text-[8px] font-bold uppercase tracking-[0.13em] text-[#597C70]">
              <Wallet size={10} />
              USDT Balance
            </div>

            <div className="mt-1.5 flex items-baseline gap-1">
              <span className="min-w-0 truncate text-[15px] font-extrabold tracking-tight text-[#3FE0B4]">
                {balance}
              </span>

              <span className="shrink-0 text-[8px] font-bold text-[#5E786F]">
                USDT
              </span>
            </div>
          </div>

          <div className="rounded-xl border border-[#F5B942]/10 bg-[#F5B942]/[0.035] p-3">
            <div className="flex items-center gap-1.5 text-[8px] font-bold uppercase tracking-[0.13em] text-[#806C45]">
              <Crown size={10} />
              VIP Level
            </div>

            <div className="mt-2 min-h-[18px]">
              {user.vip_level &&
              user.vip_level !== "None" ? (
                <span className="inline-flex max-w-full items-center gap-1 rounded-full border border-[#F5B942]/15 bg-[#F5B942]/10 px-2 py-1 text-[8px] font-bold text-[#F5C563]">
                  <Crown size={9} />
                  <span className="truncate">
                    {user.vip_level}
                  </span>
                </span>
              ) : (
                <span className="text-[10px] font-semibold text-[#59647D]">
                  No VIP
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Action */}
        <button
          type="button"
          disabled={busy}
          onClick={function () {
            props.onToggle(
              user.id,
              user.status
            );
          }}
          className={
            "mt-3 flex w-full items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-[10px] font-bold transition-all active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 " +
            (suspended
              ? "border-[#3FBF8F]/15 bg-[#3FBF8F]/10 text-[#3FBF8F] hover:border-[#3FBF8F]/30 hover:bg-[#3FBF8F]/15"
              : "border-[#E5566B]/15 bg-[#E5566B]/10 text-[#F07889] hover:border-[#E5566B]/30 hover:bg-[#E5566B]/15")
          }
        >
          {busy ? (
            <Loader2
              size={13}
              className="animate-spin"
            />
          ) : suspended ? (
            <UserCheck size={13} />
          ) : (
            <Ban size={13} />
          )}

          {suspended
            ? "Reactivate Account"
            : "Suspend Account"}
        </button>
      </div>
    </div>
  );
}

export default function Admin() {
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);

  const [pagination, setPagination] =
    useState({
      page: 1,
      limit: 5,
      total: 0,
      totalPages: 1,
    });

  const [stats, setStats] = useState({});
  const [pendingRequests, setPendingRequests] =
    useState([]);
  const [paymentMethods, setPaymentMethods] =
    useState([]);
  const [
    pendingWithdrawals,
    setPendingWithdrawals,
  ] = useState([]);

  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(null);

  const [paymentAddress, setPaymentAddress] =
    useState("");
  const [qrCode, setQrCode] = useState(null);
  const [addingPayment, setAddingPayment] =
    useState(false);
  const [
    selectedNetwork,
    setSelectedNetwork,
  ] = useState("TRC20");

  const [editingId, setEditingId] =
    useState(null);
  const [editAddress, setEditAddress] =
    useState("");
  const [editNetwork, setEditNetwork] =
    useState("TRC20");

  const [busyIds, setBusyIds] = useState(
    function () {
      return new Set();
    }
  );

  const qrInputRef = useRef(null);
  const mountedRef = useRef(true);

  const setBusy = useCallback(
    function (key, on) {
      setBusyIds(function (previous) {
        const next = new Set(previous);

        if (on) {
          next.add(key);
        } else {
          next.delete(key);
        }

        return next;
      });
    },
    []
  );

  const showNotice = useCallback(
    function (type, message) {
      setNotice({
        type: type,
        message: message,
      });

      window.setTimeout(function () {
        if (mountedRef.current) {
          setNotice(null);
        }
      }, 5000);
    },
    []
  );

  const logout = useCallback(
    function (reason) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      sessionStorage.clear();

      apiRequest("/auth/logout", {
        method: "POST",
      }).catch(function () {});

      navigate("/login", {
        replace: true,
        state: reason
          ? { reason: reason }
          : undefined,
      });
    },
    [navigate]
  );

  useEffect(
    function () {
      mountedRef.current = true;

      let role = null;

      try {
        const storedUser =
          localStorage.getItem("user");

        if (storedUser) {
          const parsed = JSON.parse(
            storedUser
          );

          role = parsed
            ? parsed.role
            : null;
        }
      } catch (e) {
        role = null;
      }

      const token =
        localStorage.getItem("token");

      if (
        !token ||
        (role && role !== "admin")
      ) {
        logout("admin-only");
      }

      return function () {
        mountedRef.current = false;
      };
    },
    [logout]
  );

  const loadData = useCallback(
    async function (options) {
      const signal =
        options && options.signal;

      setError("");

      const results =
        await Promise.allSettled([
          apiRequest(
            "/admin/users?page=" +
              pagination.page +
              "&limit=5&search=" +
              encodeURIComponent(search) +
              "&_t=" +
              Date.now(),
            {
              signal: signal,
            }
          ),

          apiRequest(
            "/admin/stats?_t=" +
              Date.now(),
            {
              signal: signal,
            }
          ),

          apiRequest(
            "/points-requests/pending",
            {
              signal: signal,
            }
          ),

          apiRequest(
            "/payment-methods/all",
            {
              signal: signal,
            }
          ),

          apiRequest(
            "/withdrawals/pending",
            {
              signal: signal,
            }
          ),
        ]);

      if (
        !mountedRef.current ||
        (signal && signal.aborted)
      ) {
        return;
      }

      const usersResult = results[0];
      const statsResult = results[1];
      const requestsResult = results[2];
      const methodsResult = results[3];
      const withdrawalsResult = results[4];

      const authFailure = results.find(
        function (result) {
          return (
            result.status === "rejected" &&
            isAuthError(result.reason)
          );
        }
      );

      if (authFailure) {
        logout("session-expired");
        return;
      }

      if (
        usersResult.status ===
          "fulfilled" &&
        usersResult.value &&
        Array.isArray(
          usersResult.value.users
        )
      ) {
        setUsers(
          usersResult.value.users
        );

        setPagination(
          usersResult.value
            .pagination || {
            page: 1,
            limit: 5,
            total: 0,
            totalPages: 1,
          }
        );
      } else {
        setUsers([]);
      }

      if (
        statsResult.status === "fulfilled"
      ) {
        setStats(
          statsResult.value || {}
        );
      } else {
        setStats({});
      }

      if (
        requestsResult.status ===
          "fulfilled" &&
        Array.isArray(
          requestsResult.value
        )
      ) {
        setPendingRequests(
          requestsResult.value
        );
      } else {
        setPendingRequests([]);
      }

      if (
        methodsResult.status ===
          "fulfilled" &&
        Array.isArray(
          methodsResult.value
        )
      ) {
        setPaymentMethods(
          methodsResult.value
        );
      } else {
        if (
          methodsResult.status ===
          "rejected"
        ) {
          console.error(
            "PAYMENT METHODS LOAD ERROR:",
            methodsResult.reason
          );
        }

        setPaymentMethods([]);
      }

      if (
        withdrawalsResult.status ===
          "fulfilled" &&
        Array.isArray(
          withdrawalsResult.value
        )
      ) {
        setPendingWithdrawals(
          withdrawalsResult.value
        );
      } else {
        setPendingWithdrawals([]);
      }

      if (
        usersResult.status ===
          "rejected" &&
        statsResult.status ===
          "rejected"
      ) {
        setError(
          describeError(
            usersResult.reason,
            "Couldn't load the admin panel."
          )
        );
      }
    },
    [
      logout,
      pagination.page,
      search,
    ]
  );

  useEffect(
    function () {
      const controller =
        new AbortController();

      loadData({
        signal: controller.signal,
      }).finally(function () {
        if (
          mountedRef.current &&
          !controller.signal.aborted
        ) {
          setLoading(false);
        }
      });

      return function () {
        controller.abort();
      };
    },
    [loadData]
  );

  useEffect(
    function () {
      const interval = setInterval(
        function () {
          loadData();
        },
        60000
      );

      return function () {
        clearInterval(interval);
      };
    },
    [loadData]
  );

  const filteredUsers = users;

  const toggleSuspend = async function (
    id,
    currentStatus
  ) {
    const key = "user:" + id;

    if (busyIds.has(key)) {
      return;
    }

    const newStatus =
      currentStatus === "suspended"
        ? "active"
        : "suspended";

    const confirmed = window.confirm(
      newStatus === "suspended"
        ? "Suspend this account?"
        : "Reactivate this account?"
    );

    if (!confirmed) {
      return;
    }

    try {
      setBusy(key, true);

      await apiRequest(
        "/admin/users/" +
          pathId(id) +
          "/status",
        {
          method: "PATCH",
          body: JSON.stringify({
            status: newStatus,
          }),
        }
      );

      await loadData();

      showNotice(
        "success",
        "Account " +
          newStatus +
          "."
      );
    } catch (err) {
      if (isAuthError(err)) {
        logout("session-expired");
        return;
      }

      showNotice(
        "error",
        describeError(
          err,
          "Couldn't update the account."
        )
      );
    } finally {
      setBusy(key, false);
    }
  };

  const reviewRequest = async function (
    id,
    action
  ) {
    const key = "request:" + id;

    if (busyIds.has(key)) {
      return;
    }

    if (
      action !== "approve" &&
      action !== "reject"
    ) {
      return;
    }

    const confirmed = window.confirm(
      action === "approve"
        ? "Approve this request?"
        : "Reject this request?"
    );

    if (!confirmed) {
      return;
    }

    try {
      setBusy(key, true);

      const response =
        await apiRequest(
          "/points-requests/" +
            pathId(id) +
            "/review",
          {
            method: "PATCH",
            headers: {
              "Idempotency-Key":
                "review-" +
                id +
                "-" +
                action,
            },
            body: JSON.stringify({
              action: action,
            }),
          }
        );

      await loadData();

      showNotice(
        "success",
        typeof response === "string"
          ? response
          : "Request " +
              action +
              "d."
      );
    } catch (err) {
      if (isAuthError(err)) {
        logout("session-expired");
        return;
      }

      showNotice(
        "error",
        describeError(
          err,
          "Couldn't review the request."
        )
      );
    } finally {
      setBusy(key, false);
    }
  };

  const reviewWithdrawal =
    async function (id, action) {
      const key = "withdrawal:" + id;

      if (busyIds.has(key)) {
        return;
      }

      if (
        action !== "approve" &&
        action !== "reject"
      ) {
        return;
      }

      const confirmed =
        window.confirm(
          action === "approve"
            ? "Approve withdrawal? User's USDT balance will be deducted."
            : "Reject this withdrawal?"
        );

      if (!confirmed) {
        return;
      }

      try {
        setBusy(key, true);

        const response =
          await apiRequest(
            "/withdrawals/" +
              pathId(id) +
              "/review",
            {
              method: "PATCH",
              headers: {
                "Idempotency-Key":
                  "withdrawal-" +
                  id +
                  "-" +
                  action,
              },
              body: JSON.stringify({
                action: action,
              }),
            }
          );

        await loadData();

        showNotice(
          "success",
          (response &&
            response.message) ||
            "Withdrawal " +
              action +
              "d successfully."
        );
      } catch (err) {
        if (isAuthError(err)) {
          logout("session-expired");
          return;
        }

        showNotice(
          "error",
          describeError(
            err,
            "Couldn't review withdrawal."
          )
        );
      } finally {
        setBusy(key, false);
      }
    };

  const handleQrChange =
    function (event) {
      const file =
        event.target.files &&
        event.target.files[0]
          ? event.target.files[0]
          : null;

      if (!file) {
        setQrCode(null);
        return;
      }

      if (
        !ALLOWED_IMAGE_TYPES.includes(
          file.type
        )
      ) {
        showNotice(
          "error",
          "QR code must be PNG, JPEG, or WebP."
        );

        event.target.value = "";
        setQrCode(null);
        return;
      }

      if (file.size > MAX_QR_BYTES) {
        showNotice(
          "error",
          "QR code must be under 2 MB."
        );

        event.target.value = "";
        setQrCode(null);
        return;
      }

      setQrCode(file);
    };

  const addPaymentMethod =
    async function (event) {
      event.preventDefault();

      if (addingPayment) {
        return;
      }

      const address =
        paymentAddress.trim();

      if (!address) {
        showNotice(
          "error",
          "Enter the payment address."
        );
        return;
      }

      if (address.length > 255) {
        showNotice(
          "error",
          "Payment address is too long."
        );
        return;
      }

      try {
        setAddingPayment(true);

        const formData =
          new FormData();

        formData.append(
          "label",
          networkLabel(
            selectedNetwork
          )
        );

        formData.append(
          "network",
          selectedNetwork
        );

        formData.append(
          "account_number",
          address
        );

        if (qrCode) {
          formData.append(
            "qr_code",
            qrCode
          );
        }

        await apiRequest(
          "/payment-methods",
          {
            method: "POST",
            body: formData,
          }
        );

        setPaymentAddress("");
        setQrCode(null);

        if (qrInputRef.current) {
          qrInputRef.current.value =
            "";
        }

        await loadData();

        showNotice(
          "success",
          "Address added successfully."
        );
      } catch (err) {
        if (isAuthError(err)) {
          logout("session-expired");
          return;
        }

        showNotice(
          "error",
          describeError(
            err,
            "Couldn't add the payment method."
          )
        );
      } finally {
        setAddingPayment(false);
      }
    };

  const deletePaymentMethod =
    async function (id) {
      const key = "method:" + id;

      if (busyIds.has(key)) {
        return;
      }

      const confirmed =
        window.confirm(
          "Delete this address permanently? This cannot be undone."
        );

      if (!confirmed) {
        return;
      }

      try {
        setBusy(key, true);

        await apiRequest(
          "/payment-methods/" +
            pathId(id),
          {
            method: "DELETE",
          }
        );

        setPaymentMethods(
          function (prev) {
            return prev.filter(
              function (m) {
                return m.id !== id;
              }
            );
          }
        );

        showNotice(
          "success",
          "Address deleted."
        );

        await loadData();
      } catch (err) {
        if (isAuthError(err)) {
          logout("session-expired");
          return;
        }

        showNotice(
          "error",
          describeError(
            err,
            "Couldn't delete the address."
          )
        );

        await loadData();
      } finally {
        setBusy(key, false);
      }
    };

  const startEdit = function (
    method
  ) {
    setEditingId(method.id);
    setEditAddress(
      method.account_number
    );
    setEditNetwork(
      method.network || "TRC20"
    );
  };

  const cancelEdit = function () {
    setEditingId(null);
    setEditAddress("");
  };

  const saveEdit = async function (
    id
  ) {
    const key = "method:" + id;

    if (busyIds.has(key)) {
      return;
    }

    const address =
      editAddress.trim();

    if (!address) {
      showNotice(
        "error",
        "Address is required."
      );
      return;
    }

    try {
      setBusy(key, true);

      const formData =
        new FormData();

      formData.append(
        "label",
        networkLabel(editNetwork)
      );

      formData.append(
        "network",
        editNetwork
      );

      formData.append(
        "account_number",
        address
      );

      await apiRequest(
        "/payment-methods/" +
          pathId(id),
        {
          method: "PUT",
          body: formData,
        }
      );

      setEditingId(null);

      await loadData();

      showNotice(
        "success",
        "Address updated."
      );
    } catch (err) {
      if (isAuthError(err)) {
        logout("session-expired");
        return;
      }

      showNotice(
        "error",
        describeError(
          err,
          "Couldn't update address."
        )
      );
    } finally {
      setBusy(key, false);
    }
  };

  const toggleMethodStatus =
    async function (
      id,
      currentStatus
    ) {
      const key = "method:" + id;

      if (busyIds.has(key)) {
        return;
      }

      const newStatus =
        currentStatus === "active"
          ? "inactive"
          : "active";

      try {
        setBusy(key, true);

        await apiRequest(
          "/payment-methods/" +
            pathId(id) +
            "/status",
          {
            method: "PATCH",
            body: JSON.stringify({
              status: newStatus,
            }),
          }
        );

        setPaymentMethods(
          function (prev) {
            return prev.map(
              function (m) {
                if (m.id === id) {
                  return Object.assign(
                    {},
                    m,
                    {
                      status:
                        newStatus,
                    }
                  );
                }

                return m;
              }
            );
          }
        );

        showNotice(
          "success",
          "Address is now " +
            newStatus +
            "."
        );

        await loadData();
      } catch (err) {
        if (isAuthError(err)) {
          logout("session-expired");
          return;
        }

        showNotice(
          "error",
          describeError(
            err,
            "Couldn't update status."
          )
        );

        await loadData();
      } finally {
        setBusy(key, false);
      }
    };

  const copyAddress = async function (
    address
  ) {
    try {
      await navigator.clipboard.writeText(
        String(address || "")
      );

      showNotice(
        "success",
        "Address copied."
      );
    } catch (err) {
      showNotice(
        "error",
        "Couldn't copy address."
      );
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center overflow-hidden bg-[#070A12] text-white">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute left-1/2 top-1/3 h-72 w-72 -translate-x-1/2 rounded-full bg-[#5B8DEF]/10 blur-[100px]" />
        </div>

        <div className="relative flex flex-col items-center">
          <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-3xl border border-[#5B8DEF]/20 bg-[#5B8DEF]/10 shadow-[0_0_50px_-15px_rgba(91,141,239,0.8)]">
            <Crown
              size={25}
              className="text-[#F5B942]"
            />
          </div>

          <Loader2
            size={20}
            className="mb-3 animate-spin text-[#6E93FF]"
          />

          <div className="text-sm font-semibold text-white">
            Loading admin panel...
          </div>

          <div className="mt-1 text-[10px] text-[#66728D]">
            Securing your dashboard
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#070A12] p-6 text-center">
        <div className="relative w-full max-w-md overflow-hidden rounded-3xl border border-[#E5566B]/20 bg-[#0E121D] p-7 shadow-2xl">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#E5566B]/10 text-[#E5566B]">
            <Ban size={23} />
          </div>

          <div className="mb-2 text-sm font-semibold text-[#F07889]">
            Access unavailable
          </div>

          <div className="text-xs leading-relaxed text-[#7C8AA5]">
            {error}
          </div>

          <div className="mt-4 rounded-xl border border-white/5 bg-white/[0.025] px-3 py-2.5 text-[10px] text-[#626D86]">
            Only admin accounts can open
            this page.
          </div>
        </div>
      </div>
    );
  }

  const totalUsers =
    Number(
      stats &&
        stats.totalUsers
    ) || 0;

  const totalUSDT =
    Number(
      stats &&
        stats.totalUSDTInCirculation
    ) || 0;

  const activeVipUsers =
    Number(
      stats &&
        stats.activeVipUsers
    ) || 0;

  const filteredMethods =
    paymentMethods.filter(
      function (m) {
        return (
          (m.network || "TRC20") ===
          selectedNetwork
        );
      }
    );

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-[#070A12] text-[#F4F6FB]">
      {/* =====================================================
          AMBIENT BACKGROUND
      ====================================================== */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -left-32 top-0 h-96 w-96 rounded-full bg-[#2D5BFF]/5 blur-[120px]" />

        <div className="absolute right-0 top-20 h-96 w-96 rounded-full bg-[#8B5CF6]/5 blur-[130px]" />

        <div className="absolute bottom-0 left-1/2 h-80 w-80 -translate-x-1/2 rounded-full bg-[#16D9A0]/[0.025] blur-[120px]" />
      </div>

      {/* =====================================================
          NOTICE
      ====================================================== */}
      {notice ? (
        <div
          role="status"
          aria-live="polite"
          className={
            "sticky top-0 z-50 flex min-h-11 items-center justify-center border-b px-4 text-center text-xs font-semibold backdrop-blur-xl " +
            (notice.type === "error"
              ? "border-[#E5566B]/20 bg-[#E5566B]/10 text-[#F07889]"
              : "border-[#3FBF8F]/20 bg-[#3FBF8F]/10 text-[#3FBF8F]")
          }
        >
          <div className="flex max-w-3xl items-center gap-2">
            {notice.type === "error" ? (
              <XCircle size={15} />
            ) : (
              <CheckCircle2 size={15} />
            )}

            <span className="truncate">
              {notice.message}
            </span>
          </div>
        </div>
      ) : null}

      {/* =====================================================
          HEADER
      ====================================================== */}
      <header
        className="sticky top-0 z-40 border-b border-white/[0.06] bg-[#090D17]/90 backdrop-blur-2xl"
        style={{
          paddingTop:
            "calc(env(safe-area-inset-top, 0px) + 12px)",
          paddingBottom: "12px",
        }}
      >
        <div className="mx-auto flex max-w-[1500px] items-center justify-between gap-4 px-4 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <div className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[#F5B942] via-[#D9A137] to-[#8062D8] shadow-[0_0_30px_-10px_rgba(245,185,66,0.8)]">
              <div className="absolute inset-[1px] rounded-2xl bg-black/10" />

              <Crown
                size={20}
                strokeWidth={2.3}
                className="relative text-[#080B12]"
              />
            </div>

            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="truncate text-sm font-extrabold tracking-tight text-white sm:text-base">
                  VIP EARN
                </h1>

                <span className="hidden rounded-full border border-[#F5B942]/20 bg-[#F5B942]/10 px-2 py-0.5 text-[8px] font-bold uppercase tracking-widest text-[#F5B942] sm:inline-flex">
                  ADMIN
                </span>
              </div>

              <p className="truncate text-[10px] text-[#68748E]">
                Control Center • Secure Admin
                Portal
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="hidden items-center gap-2 rounded-xl border border-white/[0.06] bg-white/[0.025] px-3 py-2 sm:flex">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#3FE0B4]" />

              <span className="text-[10px] font-semibold text-[#77839C]">
                System Online
              </span>
            </div>

            <button
              type="button"
              onClick={function () {
                logout();
              }}
              aria-label="Log out"
              className="group flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/[0.08] bg-white/[0.025] text-[#77839C] transition-all duration-200 hover:border-[#E5566B]/30 hover:bg-[#E5566B]/10 hover:text-[#E5566B] active:scale-95"
            >
              <LogOut
                size={16}
                className="transition-transform group-hover:-translate-x-0.5"
              />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1500px] space-y-5 p-4 sm:space-y-6 sm:p-6 lg:p-7">
        {/* =====================================================
            INTRO
        ====================================================== */}
        <div className="relative overflow-hidden rounded-3xl border border-white/[0.07] bg-gradient-to-br from-[#111827] via-[#0D121D] to-[#0A0E17] p-5 sm:p-6">
          <div className="pointer-events-none absolute -right-24 -top-32 h-72 w-72 rounded-full bg-[#5B8DEF]/10 blur-[100px]" />

          <div className="pointer-events-none absolute -bottom-32 left-1/3 h-64 w-64 rounded-full bg-[#F5B942]/5 blur-[100px]" />

          <div className="relative flex flex-col justify-between gap-5 md:flex-row md:items-center">
            <div>
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span className="rounded-full border border-[#5B8DEF]/20 bg-[#5B8DEF]/10 px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.18em] text-[#7EA2FF]">
                  Dashboard
                </span>

                <span className="text-[10px] text-[#58647D]">
                  Real-time overview
                </span>
              </div>

              <h2 className="text-xl font-extrabold tracking-tight text-white sm:text-2xl">
                Admin Control Center
              </h2>

              <p className="mt-1.5 max-w-xl text-xs leading-relaxed text-[#6F7A94]">
                Monitor users, payment methods,
                USDT deposits, withdrawals, and
                account activity from one place.
              </p>
            </div>

            <button
              type="button"
              onClick={function () {
                loadData();
              }}
              className="group flex w-full items-center justify-center gap-2 rounded-xl border border-white/[0.08] bg-white/[0.035] px-4 py-2.5 text-xs font-semibold text-[#A7B0C5] transition-all hover:border-[#5B8DEF]/30 hover:bg-[#5B8DEF]/10 hover:text-white active:scale-[0.98] md:w-auto"
            >
              <RefreshCw
                size={14}
                className="transition-transform duration-500 group-hover:rotate-180"
              />

              Refresh Data
            </button>
          </div>
        </div>

        {/* =====================================================
            STATS
        ====================================================== */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <AdminStatCard
            icon={Users}
            label="Total Users"
            value={totalUsers}
            theme="blue"
          />

          <AdminStatCard
            icon={Wallet}
            label="USDT In Circulation"
            value={
              totalUSDT.toFixed(2) +
              " USDT"
            }
            theme="green"
          />

          <AdminStatCard
            icon={TrendingUp}
            label="Active VIP Users"
            value={activeVipUsers}
            theme="gold"
          />
        </div>

        {/* =====================================================
            PAYMENT METHODS
        ====================================================== */}
        <section className="relative overflow-hidden rounded-3xl border border-white/[0.07] bg-[#0C111C]/90 p-5 shadow-[0_20px_70px_-50px_rgba(0,0,0,0.9)] sm:p-6">
          <div className="pointer-events-none absolute right-0 top-0 h-56 w-56 rounded-full bg-[#5B8DEF]/5 blur-[90px]" />

          <div className="relative">
            <SectionHeader
              icon={Globe}
              title="Crypto Payment Methods"
              description="Configure deposit networks, wallet addresses, QR codes, and availability."
              iconClass="bg-[#5B8DEF]/10 text-[#6E93FF]"
              badge={
                filteredMethods.length +
                " saved"
              }
              badgeClass="shrink-0 rounded-full border border-[#5B8DEF]/15 bg-[#5B8DEF]/10 px-2.5 py-1 text-[10px] font-semibold text-[#7EA2FF]"
            />

            {/* Network selector */}
            <div className="mb-5">
              <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.14em] text-[#77839C]">
                Select Network
              </label>

              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {NETWORK_OPTIONS.map(
                  function (net) {
                    const active =
                      selectedNetwork ===
                      net.value;

                    return (
                      <button
                        key={net.value}
                        type="button"
                        onClick={function () {
                          setSelectedNetwork(
                            net.value
                          );
                        }}
                        className={
                          "group relative overflow-hidden rounded-2xl border p-3 text-left transition-all duration-200 active:scale-[0.98] " +
                          (active
                            ? "border-[#5B8DEF]/50 bg-[#5B8DEF]/10 shadow-[0_0_25px_-12px_rgba(91,141,239,0.9)]"
                            : "border-white/[0.07] bg-white/[0.025] hover:border-white/[0.14] hover:bg-white/[0.045]")
                        }
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div
                            className={
                              "flex h-8 w-8 items-center justify-center rounded-xl text-[10px] font-black " +
                              (active
                                ? "bg-[#5B8DEF]/15 text-[#7EA2FF]"
                                : "bg-white/[0.05] text-[#68748E]")
                            }
                          >
                            {net.value ===
                            "TRC20"
                              ? "T"
                              : net.value ===
                                  "ERC20"
                                ? "E"
                                : net.value ===
                                    "BEP20"
                                  ? "B"
                                  : "TR"}
                          </div>

                          {active ? (
                            <CheckCircle2
                              size={14}
                              className="text-[#6E93FF]"
                            />
                          ) : null}
                        </div>

                        <div className="mt-2 text-[10px] font-bold leading-tight text-white">
                          {net.value}
                        </div>

                        <div className="mt-1 truncate text-[8px] text-[#65718A]">
                          {net.label.replace(
                            "USDT ",
                            ""
                          )}
                        </div>
                      </button>
                    );
                  }
                )}
              </div>
            </div>

            {/* Add payment method */}
            <form
              onSubmit={addPaymentMethod}
              className="relative mb-5 overflow-hidden rounded-2xl border border-[#2A3149] bg-[#101623] p-4 sm:p-5"
            >
              <div className="absolute right-0 top-0 h-32 w-32 rounded-full bg-[#F5B942]/5 blur-3xl" />

              <div className="relative mb-4 flex items-center justify-between gap-3">
                <div>
                  <div className="text-xs font-bold text-white">
                    Add Payment Address
                  </div>

                  <div className="mt-1 text-[10px] text-[#68748E]">
                    Adding address for{" "}
                    <span className="font-semibold text-[#F5B942]">
                      {networkLabel(
                        selectedNetwork
                      )}
                    </span>
                  </div>
                </div>

                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#F5B942]/10 text-[#F5B942]">
                  <CreditCard size={16} />
                </div>
              </div>

              <div className="grid gap-4 lg:grid-cols-[1fr_1fr]">
                <div>
                  <label
                    htmlFor="payment-address"
                    className="mb-2 block text-[10px] font-bold uppercase tracking-[0.12em] text-[#77839C]"
                  >
                    Payment Address
                  </label>

                  <input
                    id="payment-address"
                    type="text"
                    value={paymentAddress}
                    maxLength={255}
                    autoComplete="off"
                    spellCheck={false}
                    onChange={function (
                      e
                    ) {
                      setPaymentAddress(
                        e.target.value
                      );
                    }}
                    placeholder="Enter wallet address"
                    className="w-full rounded-xl border border-white/[0.08] bg-[#0A0F18] px-3.5 py-3 text-xs text-white outline-none transition-all placeholder:text-[#4F5A73] hover:border-white/[0.14] focus:border-[#5B8DEF]/60 focus:bg-[#0B111D] focus:shadow-[0_0_0_3px_rgba(91,141,239,0.08)]"
                  />

                  <div className="mt-1.5 text-right text-[9px] text-[#4F5A73]">
                    {paymentAddress.length}
                    /255
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="payment-qr-input"
                    className="mb-2 block text-[10px] font-bold uppercase tracking-[0.12em] text-[#77839C]"
                  >
                    QR Code{" "}
                    <span className="font-normal normal-case tracking-normal text-[#4F5A73]">
                      (optional)
                    </span>
                  </label>

                  <label
                    htmlFor="payment-qr-input"
                    className="group flex min-h-[74px] cursor-pointer items-center gap-3 rounded-xl border border-dashed border-[#2A3149] bg-[#0A0F18] px-4 transition hover:border-[#5B8DEF]/50 hover:bg-[#5B8DEF]/5"
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#5B8DEF]/10 text-[#6E93FF]">
                      <QrCode size={17} />
                    </div>

                    <div className="min-w-0">
                      <div className="truncate text-[11px] font-semibold text-white">
                        {qrCode
                          ? qrCode.name
                          : "Upload QR image"}
                      </div>

                      <div className="mt-0.5 text-[9px] text-[#5D6881]">
                        PNG, JPEG, WebP • Max 2
                        MB
                      </div>
                    </div>

                    <input
                      id="payment-qr-input"
                      ref={qrInputRef}
                      type="file"
                      accept={ALLOWED_IMAGE_TYPES.join(
                        ","
                      )}
                      onChange={
                        handleQrChange
                      }
                      className="hidden"
                    />
                  </label>
                </div>
              </div>

              <button
                type="submit"
                disabled={addingPayment}
                className="group mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#F5B942] to-[#D4972E] px-5 py-3 text-xs font-bold text-[#080B12] shadow-[0_12px_30px_-18px_rgba(245,185,66,0.8)] transition-all hover:-translate-y-0.5 hover:shadow-[0_18px_35px_-15px_rgba(245,185,66,0.8)] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
              >
                {addingPayment ? (
                  <Loader2
                    size={15}
                    className="animate-spin"
                  />
                ) : (
                  <Plus size={15} />
                )}

                {addingPayment
                  ? "Adding..."
                  : "Add Address"}
              </button>
            </form>

            {/* Methods */}
            {filteredMethods.length ===
            0 ? (
              <div className="rounded-2xl border border-dashed border-[#2A3149] bg-white/[0.015] py-10 text-center">
                <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-white/[0.03] text-[#4F5A73]">
                  <Globe size={19} />
                </div>

                <div className="text-xs font-semibold text-[#7C8AA5]">
                  No payment address
                  found
                </div>

                <div className="mt-1 text-[10px] text-[#4F5A73]">
                  No addresses saved for{" "}
                  {networkLabel(
                    selectedNetwork
                  )}{" "}
                  yet.
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                {filteredMethods.map(
                  function (method) {
                    const address =
                      method.account_number ||
                      "";

                    const qrUrl =
                      safeMediaUrl(
                        method.qr_image
                      );

                    const busy =
                      busyIds.has(
                        "method:" +
                          method.id
                      );

                    const isEditing =
                      editingId ===
                      method.id;

                    const isActive =
                      method.status ===
                      "active";

                    if (isEditing) {
                      return (
                        <div
                          key={method.id}
                          className="rounded-2xl border border-[#5B8DEF]/40 bg-[#101827] p-4 shadow-[0_15px_45px_-35px_rgba(91,141,239,0.8)]"
                        >
                          <div className="mb-4 flex items-center gap-2">
                            <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-[#5B8DEF]/10 text-[#6E93FF]">
                              <Pencil
                                size={14}
                              />
                            </div>

                            <div>
                              <div className="text-xs font-bold text-white">
                                Edit Payment
                                Method
                              </div>

                              <div className="text-[9px] text-[#5D6881]">
                                Update network
                                or wallet
                                address
                              </div>
                            </div>
                          </div>

                          <select
                            value={
                              editNetwork
                            }
                            onChange={function (
                              e
                            ) {
                              setEditNetwork(
                                e.target.value
                              );
                            }}
                            className="mb-3 w-full rounded-xl border border-white/[0.08] bg-[#0A0F18] px-3 py-3 text-xs text-white outline-none transition focus:border-[#5B8DEF]/60"
                          >
                            {NETWORK_OPTIONS.map(
                              function (
                                net
                              ) {
                                return (
                                  <option
                                    key={
                                      net.value
                                    }
                                    value={
                                      net.value
                                    }
                                  >
                                    {
                                      net.label
                                    }
                                  </option>
                                );
                              }
                            )}
                          </select>

                          <input
                            type="text"
                            value={
                              editAddress
                            }
                            onChange={function (
                              e
                            ) {
                              setEditAddress(
                                e.target.value
                              );
                            }}
                            className="w-full rounded-xl border border-white/[0.08] bg-[#0A0F18] px-3 py-3 text-xs text-white outline-none transition placeholder:text-[#4F5A73] focus:border-[#5B8DEF]/60"
                            placeholder="Wallet address"
                          />

                          <div className="mt-3 flex gap-2">
                            <button
                              type="button"
                              disabled={
                                busy
                              }
                              onClick={function () {
                                saveEdit(
                                  method.id
                                );
                              }}
                              className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-[#3FBF8F] px-3 py-2.5 text-xs font-bold text-[#07100D] transition hover:bg-[#4AD6A1] disabled:opacity-50"
                            >
                              {busy ? (
                                <Loader2
                                  size={13}
                                  className="animate-spin"
                                />
                              ) : (
                                <Check
                                  size={13}
                                />
                              )}

                              Save
                            </button>

                            <button
                              type="button"
                              onClick={
                                cancelEdit
                              }
                              className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-white/[0.08] bg-white/[0.04] px-3 py-2.5 text-xs font-semibold text-white transition hover:bg-white/[0.08]"
                            >
                              <X
                                size={13}
                              />

                              Cancel
                            </button>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div
                        key={method.id}
                        className={
                          "group relative overflow-hidden rounded-2xl border p-4 transition-all duration-300 " +
                          (isActive
                            ? "border-white/[0.08] bg-[#101623] hover:-translate-y-0.5 hover:border-white/[0.14]"
                            : "border-white/[0.05] bg-[#101623]/50 opacity-60")
                        }
                      >
                        <div className="pointer-events-none absolute right-0 top-0 h-32 w-32 rounded-full bg-[#5B8DEF]/5 blur-3xl" />

                        <div className="relative flex items-start justify-between gap-3">
                          <div className="flex min-w-0 items-center gap-3">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#5B8DEF]/15 to-[#8B5CF6]/10 text-[#6E93FF]">
                              <Coins size={17} />
                            </div>

                            <div className="min-w-0">
                              <div className="truncate text-xs font-bold text-white">
                                {networkLabel(
                                  method.network
                                )}
                              </div>

                              <div className="mt-1 flex items-center gap-1.5">
                                <span
                                  className={
                                    "h-1.5 w-1.5 rounded-full " +
                                    (isActive
                                      ? "bg-[#3FBF8F] shadow-[0_0_8px_rgba(63,191,143,0.8)]"
                                      : "bg-[#F5B942]")
                                  }
                                />

                                <span
                                  className={
                                    "text-[9px] font-semibold uppercase tracking-wider " +
                                    (isActive
                                      ? "text-[#3FBF8F]"
                                      : "text-[#F5B942]")
                                  }
                                >
                                  {isActive
                                    ? "Active"
                                    : "Inactive"}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="flex shrink-0 items-center gap-0.5">
                            <button
                              type="button"
                              disabled={
                                busy
                              }
                              onClick={function () {
                                toggleMethodStatus(
                                  method.id,
                                  method.status
                                );
                              }}
                              title={
                                isActive
                                  ? "Deactivate"
                                  : "Activate"
                              }
                              className="rounded-lg p-2 text-[#F5B942] transition hover:bg-[#F5B942]/10 disabled:opacity-50"
                            >
                              <Power
                                size={15}
                              />
                            </button>

                            <button
                              type="button"
                              disabled={
                                busy
                              }
                              onClick={function () {
                                startEdit(
                                  method
                                );
                              }}
                              title="Edit"
                              className="rounded-lg p-2 text-[#6E93FF] transition hover:bg-[#6E93FF]/10 disabled:opacity-50"
                            >
                              <Pencil
                                size={15}
                              />
                            </button>

                            <button
                              type="button"
                              disabled={
                                busy
                              }
                              onClick={function () {
                                deletePaymentMethod(
                                  method.id
                                );
                              }}
                              title="Delete"
                              className="rounded-lg p-2 text-[#E5566B] transition hover:bg-[#E5566B]/10 disabled:opacity-50"
                            >
                              <Trash2
                                size={15}
                              />
                            </button>
                          </div>
                        </div>

                        <div className="relative mt-4">
                          <div className="mb-1.5 flex items-center justify-between">
                            <div className="text-[9px] font-bold uppercase tracking-[0.14em] text-[#56627B]">
                              Wallet Address
                            </div>
                          </div>

                          <div className="flex items-stretch gap-2">
                            <div className="min-w-0 flex-1 break-all rounded-xl border border-white/[0.06] bg-[#090E17] p-3 text-[10px] leading-relaxed text-[#C8CFDC]">
                              {address}
                            </div>

                            <button
                              type="button"
                              onClick={function () {
                                copyAddress(
                                  address
                                );
                              }}
                              className="flex h-11 w-11 shrink-0 items-center justify-center self-center rounded-xl border border-white/[0.07] bg-[#090E17] text-[#7D89A3] transition hover:border-[#5B8DEF]/40 hover:bg-[#5B8DEF]/10 hover:text-[#6E93FF]"
                              title="Copy address"
                            >
                              <Copy
                                size={14}
                              />
                            </button>
                          </div>
                        </div>

                        <div className="relative mt-4">
                          {qrUrl ? (
                            <div className="overflow-hidden rounded-2xl border border-white/[0.06] bg-white p-4">
                              <div className="mb-3 flex items-center justify-center gap-2 text-[9px] font-bold uppercase tracking-[0.15em] text-[#727E98]">
                                <QrCode
                                  size={12}
                                />
                                Payment QR
                              </div>

                              <div className="flex justify-center">
                                <img
                                  src={
                                    qrUrl
                                  }
                                  alt={
                                    networkLabel(
                                      method.network
                                    ) +
                                    " QR"
                                  }
                                  className="h-32 w-32 object-contain"
                                />
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center gap-3 rounded-xl border border-dashed border-[#293249] bg-[#090E17] px-3 py-4 text-xs text-[#59647D]">
                              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/[0.03]">
                                <QrCode
                                  size={15}
                                />
                              </div>

                              <div>
                                <div className="text-[10px] font-semibold text-[#69748C]">
                                  No QR
                                  code
                                </div>

                                <div className="mt-0.5 text-[9px]">
                                  QR not
                                  configured
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }
                )}
              </div>
            )}
          </div>
        </section>

        {/* =====================================================
            PENDING WITHDRAWALS
        ====================================================== */}
        <section className="relative overflow-hidden rounded-3xl border border-white/[0.07] bg-[#0C111C]/90 p-5 shadow-[0_20px_70px_-50px_rgba(0,0,0,0.9)] sm:p-6">
          <div className="pointer-events-none absolute right-0 top-0 h-48 w-48 rounded-full bg-[#E5566B]/5 blur-[90px]" />

          <div className="relative">
            <SectionHeader
              icon={ArrowDownToLine}
              title="Pending Withdrawals"
              description="Verify wallet details and review user withdrawal requests."
              iconClass="bg-[#E5566B]/10 text-[#F07889]"
              badge={
                pendingWithdrawals.length +
                " pending"
              }
              badgeClass="shrink-0 rounded-full border border-[#F5B942]/15 bg-[#F5B942]/10 px-2.5 py-1 text-[10px] font-semibold text-[#F5B942]"
            />

            {pendingWithdrawals.length ===
            0 ? (
              <div className="rounded-2xl border border-dashed border-[#263047] bg-white/[0.015] py-10 text-center">
                <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-[#3FBF8F]/5 text-[#3FBF8F]/60">
                  <CheckCircle2
                    size={19}
                  />
                </div>

                <div className="text-xs font-semibold text-[#707C94]">
                  No pending withdrawals
                </div>

                <div className="mt-1 text-[10px] text-[#4F5A73]">
                  Everything is up to date.
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {pendingWithdrawals.map(
                  function (
                    withdrawal
                  ) {
                    const busy =
                      busyIds.has(
                        "withdrawal:" +
                          withdrawal.id
                      );

                    return (
                      <div
                        key={
                          withdrawal.id
                        }
                        className="group rounded-2xl border border-white/[0.07] bg-[#101623] p-4 transition-all hover:border-white/[0.12] sm:p-5"
                      >
                        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#E5566B]/10 text-[#F07889]">
                                <Wallet
                                  size={15}
                                />
                              </div>

                              <div>
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="text-xs font-bold text-white">
                                    {withdrawal.user_name ||
                                      "Unknown user"}
                                  </span>

                                  <span className="rounded-full border border-white/[0.06] bg-white/[0.025] px-2 py-0.5 text-[9px] text-[#66728B]">
                                    #
                                    {
                                      withdrawal.id
                                    }
                                  </span>
                                </div>

                                <div className="mt-0.5 text-[10px] text-[#69758E]">
                                  {withdrawal.user_email ||
                                    ""}
                                </div>
                              </div>
                            </div>

                            <div className="mt-5 grid gap-3 sm:grid-cols-2">
                              <div className="rounded-xl border border-white/[0.05] bg-[#0A0F18] p-3">
                                <div className="text-[9px] font-bold uppercase tracking-[0.12em] text-[#56627B]">
                                  Payment
                                  Method
                                </div>

                                <div className="mt-1.5 text-xs font-semibold text-white">
                                  {withdrawal.payment_method_label ||
                                    "Unknown"}
                                </div>
                              </div>

                              <div className="rounded-xl border border-[#F5B942]/10 bg-[#F5B942]/[0.03] p-3">
                                <div className="text-[9px] font-bold uppercase tracking-[0.12em] text-[#7B6A47]">
                                  Withdrawal
                                  Amount
                                </div>

                                <div className="mt-1 text-lg font-extrabold text-[#F5C563]">
                                  {Number(
                                    withdrawal.amount_usdt ||
                                      0
                                  ).toFixed(
                                    2
                                  )}{" "}
                                  <span className="text-[10px] font-semibold">
                                    USDT
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className="mt-4">
                              <div className="mb-1.5 flex items-center justify-between">
                                <div className="text-[9px] font-bold uppercase tracking-[0.12em] text-[#56627B]">
                                  User Wallet /
                                  Exchange
                                </div>
                              </div>

                              <div className="flex items-stretch gap-2">
                                <div className="min-w-0 flex-1 break-all rounded-xl border border-white/[0.06] bg-[#090E17] p-3 text-[10px] leading-relaxed text-[#D5DAE4]">
                                  {withdrawal.wallet_address ||
                                    "No address provided"}
                                </div>

                                <button
                                  type="button"
                                  disabled={
                                    busy
                                  }
                                  onClick={function () {
                                    copyAddress(
                                      withdrawal.wallet_address
                                    );
                                  }}
                                  className="flex h-11 w-11 shrink-0 items-center justify-center self-center rounded-xl border border-white/[0.07] bg-[#090E17] text-[#7D89A3] transition hover:border-[#5B8DEF]/40 hover:bg-[#5B8DEF]/10 hover:text-[#6E93FF] disabled:opacity-50"
                                >
                                  <Copy
                                    size={
                                      14
                                    }
                                  />
                                </button>
                              </div>
                            </div>

                            {withdrawal.created_at ? (
                              <div className="mt-3 flex items-center gap-1.5 text-[9px] text-[#59647D]">
                                <Clock3
                                  size={11}
                                />

                                Requested:{" "}
                                {new Date(
                                  withdrawal.created_at
                                ).toLocaleString()}
                              </div>
                            ) : null}
                          </div>

                          <div className="flex w-full flex-row gap-2 lg:w-36 lg:flex-col">
                            <button
                              type="button"
                              disabled={
                                busy
                              }
                              onClick={function () {
                                copyAddress(
                                  withdrawal.wallet_address
                                );
                              }}
                              className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-white/[0.07] bg-[#090E17] px-4 py-2.5 text-[10px] font-bold text-[#8994AA] transition hover:border-[#5B8DEF]/40 hover:bg-[#5B8DEF]/10 hover:text-[#6E93FF] disabled:opacity-50 lg:flex-none"
                            >
                              <Copy
                                size={13}
                              />
                              Copy
                            </button>

                            <button
                              type="button"
                              disabled={
                                busy
                              }
                              onClick={function () {
                                reviewWithdrawal(
                                  withdrawal.id,
                                  "approve"
                                );
                              }}
                              className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-[#3FBF8F] px-4 py-2.5 text-[10px] font-bold text-[#07100D] transition hover:bg-[#4AD6A1] disabled:opacity-50 lg:flex-none"
                            >
                              {busy ? (
                                <Loader2
                                  size={13}
                                  className="animate-spin"
                                />
                              ) : (
                                <CheckCircle2
                                  size={
                                    13
                                  }
                                />
                              )}

                              Approve
                            </button>

                            <button
                              type="button"
                              disabled={
                                busy
                              }
                              onClick={function () {
                                reviewWithdrawal(
                                  withdrawal.id,
                                  "reject"
                                );
                              }}
                              className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-[#E5566B]/20 bg-[#E5566B]/10 px-4 py-2.5 text-[10px] font-bold text-[#F07889] transition hover:bg-[#E5566B]/20 disabled:opacity-50 lg:flex-none"
                            >
                              <XCircle
                                size={13}
                              />
                              Reject
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  }
                )}
              </div>
            )}
          </div>
        </section>

        {/* =====================================================
            USDT REQUESTS
        ====================================================== */}
        <section className="relative overflow-hidden rounded-3xl border border-white/[0.07] bg-[#0C111C]/90 p-5 shadow-[0_20px_70px_-50px_rgba(0,0,0,0.9)] sm:p-6">
          <div className="pointer-events-none absolute left-0 top-0 h-48 w-48 rounded-full bg-[#3FE0B4]/5 blur-[90px]" />

          <div className="relative">
            <SectionHeader
              icon={Coins}
              title="Pending USDT Requests"
              description="Review user payment proofs and approve or reject deposits."
              iconClass="bg-[#3FE0B4]/10 text-[#3FE0B4]"
              badge={
                pendingRequests.length +
                " pending"
              }
              badgeClass="shrink-0 rounded-full border border-[#F5B942]/15 bg-[#F5B942]/10 px-2.5 py-1 text-[10px] font-semibold text-[#F5B942]"
            />

            {pendingRequests.length ===
            0 ? (
              <div className="rounded-2xl border border-dashed border-[#263047] bg-white/[0.015] py-10 text-center">
                <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-[#3FE0B4]/5 text-[#3FE0B4]/60">
                  <CheckCircle2
                    size={19}
                  />
                </div>

                <div className="text-xs font-semibold text-[#707C94]">
                  No pending requests
                </div>

                <div className="mt-1 text-[10px] text-[#4F5A73]">
                  All deposit requests have
                  been reviewed.
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {pendingRequests.map(
                  function (request) {
                    const busy =
                      busyIds.has(
                        "request:" +
                          request.id
                      );

                    const screenshotUrl =
                      safeMediaUrl(
                        request.attachment_path
                      ) || "#";

                    return (
                      <div
                        key={
                          request.id
                        }
                        className="rounded-2xl border border-white/[0.07] bg-[#101623] p-4 transition-all hover:border-white/[0.12] sm:p-5"
                      >
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-3">
                              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#3FE0B4]/10 text-[#3FE0B4]">
                                <Coins
                                  size={15}
                                />
                              </div>

                              <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="text-xs font-bold text-white">
                                    {request.user_name ||
                                      "Unknown user"}
                                  </span>

                                  <span className="rounded-full border border-white/[0.06] bg-white/[0.025] px-2 py-0.5 text-[9px] text-[#66728B]">
                                    #
                                    {
                                      request.id
                                    }
                                  </span>
                                </div>

                                <div className="mt-0.5 truncate text-[10px] text-[#69758E]">
                                  {request.user_email ||
                                    ""}
                                </div>
                              </div>
                            </div>

                            <div className="mt-4 flex flex-wrap items-center gap-3">
                              <div className="rounded-xl border border-[#3FBF8F]/10 bg-[#3FBF8F]/5 px-3 py-2">
                                <div className="text-[8px] font-bold uppercase tracking-[0.12em] text-[#597C70]">
                                  Amount
                                </div>

                                <div className="mt-0.5 text-base font-extrabold text-[#3FE0B4]">
                                  {Number(
                                    request.amount_usdt ||
                                      0
                                  ).toFixed(
                                    2
                                  )}{" "}
                                  <span className="text-[9px]">
                                    USDT
                                  </span>
                                </div>
                              </div>

                              {request.note ? (
                                <div className="min-w-0 max-w-xl flex-1 rounded-xl border border-white/[0.05] bg-white/[0.02] px-3 py-2">
                                  <div className="mb-0.5 text-[8px] font-bold uppercase tracking-[0.12em] text-[#56627B]">
                                    Note
                                  </div>

                                  <div className="break-words text-[10px] leading-relaxed text-[#929CB0]">
                                    {
                                      request.note
                                    }
                                  </div>
                                </div>
                              ) : null}
                            </div>

                            {request.attachment_path ? (
                              <ScreenshotLink
                                href={
                                  screenshotUrl
                                }
                              />
                            ) : (
                              <div className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-[#F5B942]/10 bg-[#F5B942]/5 px-2.5 py-1.5 text-[9px] font-semibold text-[#8C7545]">
                                <ImageIcon
                                  size={11}
                                />
                                Proof unavailable
                              </div>
                            )}
                          </div>

                          <div className="flex w-full shrink-0 items-center gap-2 lg:w-auto">
                            <button
                              type="button"
                              disabled={
                                busy
                              }
                              onClick={function () {
                                reviewRequest(
                                  request.id,
                                  "approve"
                                );
                              }}
                              className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-[#3FBF8F] px-4 py-2.5 text-[10px] font-bold text-[#07100D] transition hover:bg-[#4AD6A1] active:scale-[0.98] disabled:opacity-50 sm:flex-none"
                            >
                              {busy ? (
                                <Loader2
                                  size={13}
                                  className="animate-spin"
                                />
                              ) : (
                                <CheckCircle2
                                  size={
                                    13
                                  }
                                />
                              )}

                              Approve
                            </button>

                            <button
                              type="button"
                              disabled={
                                busy
                              }
                              onClick={function () {
                                reviewRequest(
                                  request.id,
                                  "reject"
                                );
                              }}
                              className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-[#E5566B]/20 bg-[#E5566B]/10 px-4 py-2.5 text-[10px] font-bold text-[#F07889] transition hover:bg-[#E5566B]/20 active:scale-[0.98] disabled:opacity-50 sm:flex-none"
                            >
                              <XCircle
                                size={13}
                              />

                              Reject
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  }
                )}
              </div>
            )}
          </div>
        </section>

        {/* =====================================================
            USERS
        ====================================================== */}
        <section className="relative overflow-hidden rounded-3xl border border-white/[0.07] bg-[#0C111C]/90 p-4 shadow-[0_20px_70px_-50px_rgba(0,0,0,0.9)] sm:p-6">
          <div className="pointer-events-none absolute right-0 top-0 h-48 w-48 rounded-full bg-[#2D5BFF]/5 blur-[90px]" />

          <div className="relative">
            {/* Users header */}
            <div className="mb-5 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex min-w-0 items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[#8B5CF6]/10 text-[#A98BFF]">
                  <Users size={18} />
                </div>

                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h2 className="text-[15px] font-bold text-white">
                      Users
                    </h2>

                    <span className="rounded-full border border-[#8B5CF6]/15 bg-[#8B5CF6]/10 px-2 py-0.5 text-[8px] font-bold text-[#A98BFF]">
                      {pagination.total ||
                        users.length}
                    </span>
                  </div>

                  <p className="mt-1 text-[11px] leading-relaxed text-[#727E98]">
                    Manage registered users
                    and account status.
                  </p>
                </div>
              </div>

              {/* Search */}
              <div className="relative w-full md:w-80">
                <Search
                  size={14}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#66728B]"
                />

                <input
                  type="text"
                  value={search}
                  onChange={function (e) {
                    setSearch(
                      e.target.value
                    );

                    setPagination(
                      function (p) {
                        return Object.assign(
                          {},
                          p,
                          {
                            page: 1,
                          }
                        );
                      }
                    );
                  }}
                  placeholder="Search name or email..."
                  className="w-full rounded-xl border border-white/[0.08] bg-[#101623] py-3 pl-10 pr-4 text-xs text-white outline-none transition placeholder:text-[#4F5A73] hover:border-white/[0.14] focus:border-[#5B8DEF]/60 focus:bg-[#111827] focus:shadow-[0_0_0_3px_rgba(91,141,239,0.06)]"
                />
              </div>
            </div>

            {filteredUsers.length ===
            0 ? (
              <div className="rounded-2xl border border-dashed border-[#263047] bg-white/[0.015] py-10 text-center">
                <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-white/[0.03] text-[#4F5A73]">
                  <Users size={19} />
                </div>

                <div className="text-xs font-semibold text-[#707C94]">
                  No users found
                </div>

                <div className="mt-1 text-[10px] text-[#4F5A73]">
                  Try a different name or
                  email.
                </div>
              </div>
            ) : (
              <>
                {/* =================================================
                    MOBILE USER CARDS
                ================================================== */}
                <div className="space-y-3 md:hidden">
                  {filteredUsers.map(
                    function (user) {
                      return (
                        <MobileUserCard
                          key={user.id}
                          user={user}
                          busy={busyIds.has(
                            "user:" +
                              user.id
                          )}
                          onToggle={
                            toggleSuspend
                          }
                        />
                      );
                    }
                  )}
                </div>

                {/* =================================================
                    DESKTOP TABLE
                ================================================== */}
                <div className="hidden md:block">
                  <div className="overflow-hidden rounded-2xl border border-white/[0.06]">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="border-b border-white/[0.07] bg-[#101623] text-[9px] font-bold uppercase tracking-[0.12em] text-[#66728B]">
                          <th className="px-4 py-3.5">
                            User
                          </th>

                          <th className="px-4 py-3.5">
                            USDT Balance
                          </th>

                          <th className="px-4 py-3.5">
                            VIP
                          </th>

                          <th className="px-4 py-3.5">
                            Status
                          </th>

                          <th className="px-4 py-3.5">
                            Action
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {filteredUsers.map(
                          function (user) {
                            const busy =
                              busyIds.has(
                                "user:" +
                                  user.id
                              );

                            const suspended =
                              user.status ===
                              "suspended";

                            return (
                              <tr
                                key={
                                  user.id
                                }
                                className="border-b border-white/[0.045] bg-[#0C111B] transition hover:bg-[#101623] last:border-b-0"
                              >
                                <td className="px-4 py-4">
                                  <div className="flex items-center gap-3">
                                    <div
                                      className={
                                        "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br text-[11px] font-bold " +
                                        (suspended
                                          ? "from-[#E5566B]/15 to-[#7F3140]/10 text-[#F07889]"
                                          : "from-[#5B8DEF]/15 to-[#8B5CF6]/10 text-[#7EA2FF]")
                                      }
                                    >
                                      {String(
                                        user.name ||
                                          "U"
                                      )
                                        .charAt(
                                          0
                                        )
                                        .toUpperCase()}
                                    </div>

                                    <div className="min-w-0">
                                      <div className="truncate text-xs font-semibold text-white">
                                        {user.name ||
                                          "Unknown"}
                                      </div>

                                      <div className="mt-1 max-w-[250px] truncate text-[10px] text-[#68748E]">
                                        {user.email ||
                                          ""}
                                      </div>
                                    </div>
                                  </div>
                                </td>

                                <td className="px-4 py-4">
                                  <div className="whitespace-nowrap text-xs font-bold text-[#3FE0B4]">
                                    {Number(
                                      user.usdt_balance ||
                                        0
                                    ).toFixed(
                                      2
                                    )}{" "}
                                    <span className="text-[9px] font-medium text-[#5E786F]">
                                      USDT
                                    </span>
                                  </div>
                                </td>

                                <td className="px-4 py-4">
                                  {user.vip_level &&
                                  user.vip_level !==
                                    "None" ? (
                                    <span className="inline-flex items-center gap-1.5 rounded-full border border-[#F5B942]/15 bg-[#F5B942]/10 px-2.5 py-1 text-[9px] font-bold text-[#F5C563]">
                                      <Crown
                                        size={
                                          10
                                        }
                                      />

                                      {user.vip_level}
                                    </span>
                                  ) : (
                                    <span className="text-xs text-[#56627B]">
                                      —
                                    </span>
                                  )}
                                </td>

                                <td className="px-4 py-4">
                                  <span
                                    className={
                                      "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider " +
                                      (suspended
                                        ? "border-[#E5566B]/15 bg-[#E5566B]/10 text-[#F07889]"
                                        : "border-[#3FBF8F]/15 bg-[#3FBF8F]/10 text-[#3FBF8F]")
                                    }
                                  >
                                    <span
                                      className={
                                        "h-1.5 w-1.5 rounded-full " +
                                        (suspended
                                          ? "bg-[#E5566B]"
                                          : "bg-[#3FBF8F]")
                                      }
                                    />

                                    {user.status ||
                                      "active"}
                                  </span>
                                </td>

                                <td className="px-4 py-4">
                                  <button
                                    type="button"
                                    disabled={
                                      busy
                                    }
                                    onClick={function () {
                                      toggleSuspend(
                                        user.id,
                                        user.status
                                      );
                                    }}
                                    className={
                                      "inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-[9px] font-bold transition disabled:opacity-50 " +
                                      (suspended
                                        ? "border-[#3FBF8F]/15 bg-[#3FBF8F]/10 text-[#3FBF8F] hover:bg-[#3FBF8F]/20"
                                        : "border-[#E5566B]/15 bg-[#E5566B]/10 text-[#E5566B] hover:bg-[#E5566B]/20")
                                    }
                                  >
                                    {busy ? (
                                      <Loader2
                                        size={
                                          12
                                        }
                                        className="animate-spin"
                                      />
                                    ) : suspended ? (
                                      <UserCheck
                                        size={
                                          12
                                        }
                                      />
                                    ) : (
                                      <Ban
                                        size={
                                          12
                                        }
                                      />
                                    )}

                                    {suspended
                                      ? "Activate"
                                      : "Suspend"}
                                  </button>
                                </td>
                              </tr>
                            );
                          }
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {/* =====================================================
                PAGINATION
            ====================================================== */}
            {pagination.totalPages >
            1 ? (
              <div className="mt-5 flex flex-col gap-3 border-t border-white/[0.05] pt-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-center text-[10px] text-[#59647D] sm:text-left">
                  Page{" "}
                  <span className="font-semibold text-[#8B96AA]">
                    {pagination.page}
                  </span>{" "}
                  of{" "}
                  <span className="font-semibold text-[#8B96AA]">
                    {pagination.totalPages}
                  </span>

                  {pagination.total ? (
                    <span className="ml-1">
                      • {pagination.total} users
                    </span>
                  ) : null}
                </div>

                <div className="flex items-center justify-center gap-1.5">
                  {/* Previous */}
                  <button
                    type="button"
                    disabled={
                      pagination.page <=
                      1
                    }
                    onClick={function () {
                      setPagination(
                        function (p) {
                          return Object.assign(
                            {},
                            p,
                            {
                              page:
                                p.page -
                                1,
                            }
                          );
                        }
                      );
                    }}
                    className="flex h-9 items-center justify-center gap-1 rounded-xl border border-white/[0.07] bg-white/[0.025] px-2.5 text-[9px] font-semibold text-[#7C879E] transition hover:border-[#5B8DEF]/40 hover:bg-[#5B8DEF]/5 hover:text-white active:scale-95 disabled:pointer-events-none disabled:opacity-25 sm:px-3"
                  >
                    <ChevronLeft
                      size={14}
                    />

                    <span className="hidden sm:inline">
                      Previous
                    </span>
                  </button>

                  {/* Page numbers */}
                  <div className="flex items-center gap-1">
                    {Array.from(
                      {
                        length:
                          pagination.totalPages,
                      },
                      function (_, i) {
                        return i + 1;
                      }
                    ).map(
                      function (p) {
                        return (
                          <button
                            key={p}
                            type="button"
                            onClick={function () {
                              setPagination(
                                function (
                                  prev
                                ) {
                                  return Object.assign(
                                    {},
                                    prev,
                                    {
                                      page:
                                        p,
                                    }
                                  );
                                }
                              );
                            }}
                            className={
                              "flex h-9 min-w-9 items-center justify-center rounded-xl px-2 text-[9px] font-bold transition active:scale-95 " +
                              (p ===
                              pagination.page
                                ? "bg-[#5B8DEF] text-white shadow-[0_6px_18px_-8px_rgba(91,141,239,0.9)]"
                                : "border border-white/[0.07] bg-white/[0.025] text-[#707C94] hover:border-[#5B8DEF]/40 hover:bg-[#5B8DEF]/5 hover:text-white")
                            }
                          >
                            {p}
                          </button>
                        );
                      }
                    )}
                  </div>

                  {/* Next */}
                  <button
                    type="button"
                    disabled={
                      pagination.page >=
                      pagination.totalPages
                    }
                    onClick={function () {
                      setPagination(
                        function (p) {
                          return Object.assign(
                            {},
                            p,
                            {
                              page:
                                p.page +
                                1,
                            }
                          );
                        }
                      );
                    }}
                    className="flex h-9 items-center justify-center gap-1 rounded-xl border border-white/[0.07] bg-white/[0.025] px-2.5 text-[9px] font-semibold text-[#7C879E] transition hover:border-[#5B8DEF]/40 hover:bg-[#5B8DEF]/5 hover:text-white active:scale-95 disabled:pointer-events-none disabled:opacity-25 sm:px-3"
                  >
                    <span className="hidden sm:inline">
                      Next
                    </span>

                    <ChevronRight
                      size={14}
                    />
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </section>

        {/* =====================================================
            FOOTER
        ====================================================== */}
        <div className="flex flex-col items-center justify-between gap-3 rounded-2xl border border-white/[0.05] bg-white/[0.015] px-4 py-3 sm:flex-row">
          <div className="flex items-center gap-2 text-[9px] text-[#58647D]">
            <Server size={12} />

            VIP EARN Admin Control Center
          </div>

          <div className="flex items-center gap-1.5 text-[9px] text-[#59647D]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#3FBF8F] shadow-[0_0_7px_rgba(63,191,143,0.65)]" />

            All systems operational
          </div>
        </div>
      </main>
    </div>
  );
}